import React, { useState, useCallback, useEffect } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { isLoggedIn } from '../../api/auth';

interface SignInFormProps {
  onLoginSuccess?: () => void;
  onLoginError?: (error: string) => void;
  redirectPath?: string;
}

const SignInForm: React.FC<SignInFormProps> = ({ 
  onLoginSuccess, 
  onLoginError,
  redirectPath 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { login, loading, error } = useAuth();
  const { t } = useLanguage();

  // Forward auth context errors to parent component
  useEffect(() => {
    if (error && onLoginError) {
      onLoginError(error);
    }
  }, [error, onLoginError]);

  // Create a login function that works with the localStorage approach
  const handleLogin = useCallback(async () => {
    if (!username || !password) {
      onLoginError?.(t('signin.errorUserPass'));
      return;
    }
    
    try {
      // Try to log in with optional redirect path
      try {
        await login(username, password, redirectPath);
        
        // If we get here, login was successful
        console.log('Login successful');
        
        // Call the success callback from parent
        onLoginSuccess?.();
      } catch (err: any) {
        console.error('Login error:', err);
        
        // Before showing error, verify if login actually worked despite the error
        // by checking authentication status
        setTimeout(async () => {
          try {
            const authenticated = await isLoggedIn();
            
            if (authenticated) {
              console.log('Login successful after verification');
              // Login actually worked, redirect user
              window.location.href = redirectPath || '/dashboard';
            } else {
              // Still not authenticated, show error
              let message = t('signin.invalidCredentials');
              
              if (err.response?.data?.detail) {
                message = t('signin.loginFailed', { message: err.response.data.detail });
              } else if (err.message) {
                message = t('signin.loginFailed', { message: err.message });
              }
              
              onLoginError?.(message);
            }
          } catch (verifyError) {
            // Verification failed, show original error
            let message = t('signin.invalidCredentials');
            
            if (err.response?.data?.detail) {
              message = t('signin.loginFailed', { message: err.response.data.detail });
            } else if (err.message) {
              message = t('signin.loginFailed', { message: err.message });
            }
            
            onLoginError?.(message);
          }
        }, 500); // Small delay to allow backend to complete auth process
      }
    } catch (unexpectedErr) {
      console.error('Unexpected login error:', unexpectedErr);
      onLoginError?.(t('signin.unexpectedError'));
    }
  }, [username, password, login, redirectPath, onLoginSuccess, onLoginError, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleLogin();
    return false;
  };

  return (
    <div className="space-y-5 sm:space-y-6 bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-100">
      <div>
        <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-1.5">
          {t('signin.username')}
        </label>
        <input
          type="text"
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow bg-gray-50 hover:bg-white focus:bg-white"
          placeholder={t('signin.usernamePlace')}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) {
              e.preventDefault();
              handleLogin();
            }
          }}
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1.5">
          {t('signin.password')}
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow bg-gray-50 hover:bg-white focus:bg-white"
            placeholder={t('signin.passwordPlace')}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                e.preventDefault();
                handleLogin();
              }
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors"
            disabled={loading}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-end">
        <a href="#" className="text-sm text-blue-600 hover:text-blue-700 transition-colors font-medium hover:underline">
          {t('signin.forgotPassword')}
        </a>
      </div>

      <button
        type="button" 
        onClick={handleSubmit}
        className="w-full bg-blue-600 text-white py-2.5 sm:py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/50 transition-all transform active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed font-medium text-sm shadow-md"
        disabled={loading}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <Loader2 size={20} className="animate-spin mr-2" />
            {t('signin.signingIn')}
          </span>
        ) : (
          t('signin.signIn')
        )}
      </button>
    </div>
  );
};

export default SignInForm;